import { RecruiterCv, RecruiterCvUpload } from "../types/recruiter.entity";

/**
 * A path this learner wrote, inside their own folder.
 *
 * An ADMIN may read every CV in the bucket, so the check is not about
 * permission. It is about this page only ever opening the file belonging to the
 * learner whose record is on screen.
 */
export const ownedByLearner = (storagePath: string | undefined, userId: string): boolean =>
  !!storagePath && storagePath.startsWith(`job_cvs/${userId}/`) && !storagePath.includes("..");

type PickCvInput = {
  userId: string;
  /**
   * Ledger rows for this record's conversation, newest first.
   *
   * `null` means the ledger could not be read, which is different from an empty
   * array: empty is evidence that no CV was recorded against this conversation,
   * null is the absence of evidence either way.
   */
  uploads: RecruiterCvUpload[] | null;
  /** `job_candidates.cvStoragePath` — the learner's current CV, any conversation. */
  candidateCvPath?: string;
};

/**
 * Which CV to show on one record, and how firmly it belongs to it.
 *
 * The decision, with no I/O, because it is the part that was wrong. The page
 * used to take `job_candidates.cvStoragePath` as this record's CV. That row is
 * keyed on the learner and holds only their *current* CV, so a screenshot
 * uploaded on 2026-09-20 appeared under every later application from that
 * account, including ones that carried no CV at all.
 *
 * The ledger is the answer because it is the only place `conversationId` is
 * written beside a storage path. The candidate row stays as a **labelled**
 * fallback rather than being dropped: the ledger has no row for anything
 * uploaded before it existed, so an empty result is not proof that no CV was
 * sent. The caller is told which of the two it got so the page can say so.
 */
export const pickCv = ({ userId, uploads, candidateCvPath }: PickCvInput): RecruiterCv | null => {
  const fallback = (source: RecruiterCv["source"]): RecruiterCv | null =>
    ownedByLearner(candidateCvPath, userId) ? { storagePath: candidateCvPath as string, source } : null;

  if (uploads === null) return fallback("PROFILE_UNVERIFIED");

  // FOLDER rows are a failed account-deletion sweep, not a CV anyone can open.
  const thisRecord = uploads.find(
    (upload) =>
      upload.kind === "FILE" && upload.userId === userId && ownedByLearner(upload.storagePath, userId)
  );

  if (thisRecord) return { storagePath: thisRecord.storagePath, source: "THIS_RECORD" };

  return fallback("PROFILE");
};
